import { useContext } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ActiveDot } from '@/components/ui/layout/active-dot';
import { cn } from '@/lib/utils';

import { FeedAge } from './FeedAge';
import { chatActionOf, ctaIconOf } from './feedCta';
import { FeedIcon } from './FeedIcon';
import { FeedIconCheck } from './FeedIconCheck';
import { capitalized, feedKindOf, TODO_KIND } from './feedKind';
import { FeedOpenLink } from './FeedOpenLink';
import { FeedOutcome } from './FeedOutcome';
import { FeedRowAction } from './FeedRowActions';
import { FeedRowRoleContext } from './feedRowRole';
import { FeedRowTitle } from './FeedRowTitle';
import { FeedRunningPill } from './FeedRunningPill';
import { snoozeWhenLabel } from './feedSnooze';
import {
  FEED_CHIP_CLASS,
  FEED_ROW_AGE_CLASS,
  FEED_ROW_GRID,
  FEED_ROW_LINE_CLASS,
  FEED_ROW_META_CLASS,
  FEED_ROW_SURFACE_CLASS,
  FEED_ROW_TRAY_CLASS,
  STAKE_PILL_CLASS,
} from './feedStyles';
import { FeedSubtasksChip } from './FeedSubtasksChip';
import { FeedTrayCta } from './FeedTrayCta';
import { useRowCheck } from './useRowCheck';

import type { FeedRowProps } from './FeedRow';

const SHOWS_WAITING_IN_LABEL = false;

const TRAY_CTA_SPACING = 'ml-[calc(4*var(--ac-u))]';

// The age ends where the tray's chevron draws its stroke, 7u inside the chevron button's edge
const ROW_END_CLASS =
  'pointer-events-none relative z-[1] mr-[calc(var(--ac-row-end-inset,0px)_+_7*var(--ac-u))] flex h-[calc(28*var(--ac-u))] items-center gap-[calc(6*var(--ac-u))] [&_a]:pointer-events-auto [&_button]:pointer-events-auto';

export const FeedCompactRow = ({
  view,
  icon,
  todo = false,
  struck = false,
  appName,
  onOpen,
  onDismiss,
  onChat,
  chatLabel = 'Chat',
  chatStarted = false,
  hoverActions,
  label,
  labelDot,
  labelAccessory,
  staffLabel,
  backAt,
  onMarkDone,
  checkKind = 'box',
  checkLabel = 'Mark done',
  hoverless = false,
  watchSuggestion,
  subtasks,
  onPress,
  onOpenUrl,
  onKeyDown,
}: Omit<FeedRowProps, 'description'>) => {
  const step = useContext(FeedRowRoleContext) === 'step';
  const { pill, outcome, stake } = view;
  const kind = todo ? TODO_KIND : feedKindOf(view.verb);
  const CtaIcon = pill ? ctaIconOf(pill, kind.icon) : null;
  const labelStake =
    SHOWS_WAITING_IN_LABEL && stake?.tone === 'muted' ? stake : null;
  const urgentStake = stake?.tone === 'muted' ? null : stake;

  const openLink =
    outcome?.tone === 'done' && view.openUrl ? (
      <FeedOpenLink
        url={view.openUrl}
        appName={appName}
        onOpenUrl={onOpenUrl}
      />
    ) : null;
  const actionable = !outcome && !view.running;
  const { checking, check } = useRowCheck(onMarkDone);
  const primary =
    pill && CtaIcon && pill.press.kind !== 'acknowledge' ? (
      <FeedTrayCta
        icon={CtaIcon}
        label={pill.label}
        accessibleLabel={`${pill.label} ${view.title}`}
        className={TRAY_CTA_SPACING}
        onClick={() => onPress(pill.press)}
      />
    ) : null;
  const dismiss = onDismiss ? (
    <FeedRowAction
      icon={Icons.trash}
      label="Dismiss"
      accessibleLabel={`Dismiss ${view.title}`}
      hotkey={['mod', 'backspace']}
      onClick={onDismiss}
    />
  ) : null;
  // The row's icon checks it off, so hover shows Dismiss, Pin, Snooze and ⋯ with Assign. A row that opens
  // leaves its primary and Chat to the open card, and opens from anywhere on the row; one that does not keeps them
  const trayActions = onOpen ? (
    <>
      {dismiss}
      {hoverActions}
    </>
  ) : (
    <>
      {onChat ? (
        <FeedRowAction
          {...chatActionOf(view.title, chatLabel, chatStarted)}
          hotkey={['mod', 'j']}
          onClick={onChat}
        />
      ) : null}
      {dismiss}
      {hoverActions}
      {primary}
    </>
  );
  const hasTray =
    actionable &&
    !hoverless &&
    !checking &&
    Boolean(
      onOpen
        ? onDismiss || hoverActions
        : pill || onDismiss || onChat || hoverActions
    );
  const runningPill =
    pill && view.running ? (
      <FeedRunningPill pill={pill} quiet={step} onPress={onPress} />
    ) : null;
  const rowEnd = (
    <>
      <span data-feed-row-end="" className={ROW_END_CLASS}>
        {outcome ? null : (
          <span className={FEED_ROW_AGE_CLASS}>
            {backAt ? (
              `Back ${snoozeWhenLabel(backAt, new Date())}`
            ) : (
              <FeedAge at={view.age} />
            )}
          </span>
        )}
        {openLink}
        {runningPill}
      </span>
      {hasTray ? (
        <span
          role="group"
          aria-label={`Actions for ${view.title}`}
          data-feed-row-tray=""
          className={FEED_ROW_TRAY_CLASS}
        >
          {trayActions}
        </span>
      ) : null}
    </>
  );

  return (
    <div
      data-testid="feed-row"
      className={cn(
        FEED_ROW_GRID,
        FEED_ROW_SURFACE_CLASS,
        (onOpen || hasTray) && 'hover:bg-surface-hover'
      )}
    >
      <span className="mt-[calc(2*var(--ac-u))]">
        {actionable && onMarkDone ? (
          <FeedIconCheck
            title={view.title}
            kind={checkKind}
            label={checkLabel}
            failed={view.failed}
            ticked={checking}
            onCheck={check ?? onMarkDone}
          >
            {icon}
          </FeedIconCheck>
        ) : (
          <FeedIcon failed={view.failed}>{icon}</FeedIcon>
        )}
      </span>

      <span className="flex min-w-0">
        <span className={cn(FEED_ROW_LINE_CLASS, 'flex-1')}>
          <FeedRowTitle
            title={todo ? capitalized(view.title) : view.title}
            struck={struck}
            striking={checking}
            onOpen={onOpen}
            onKeyDown={onKeyDown}
          />
          {outcome ? (
            <FeedOutcome outcome={outcome} />
          ) : (
            <>
              {label || labelDot ? (
                <span
                  data-testid="feed-row-meta"
                  className={FEED_ROW_META_CLASS}
                >
                  {labelDot ? (
                    <ActiveDot className="mr-[calc(4*var(--ac-u))] inline-block bg-blue-500 align-middle" />
                  ) : null}
                  {label}
                  {labelStake ? ` · ${labelStake.label}` : null}
                </span>
              ) : null}
              {labelAccessory}
              {watchSuggestion}
              {urgentStake ? (
                <span
                  className={cn(
                    FEED_CHIP_CLASS,
                    'min-w-0 shrink-[10] truncate',
                    STAKE_PILL_CLASS[urgentStake.tone]
                  )}
                >
                  {urgentStake.label}
                </span>
              ) : null}
            </>
          )}
          {staffLabel}
          {subtasks ? <FeedSubtasksChip progress={subtasks} /> : null}
        </span>
      </span>

      {rowEnd}
    </div>
  );
};
